import { FC, useEffect, useState, useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import { Table } from "@vilocnv/allsetra-core";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";

// Data
import {
  useAppDispatch,
  useAppSelector,
  useReportCommonStates,
  useReportInactiveObjects
} from "hooks";
import {
  commonReportsDataFormatter,
  commonReportsFilterFormValidationSchema,
  co2ReportsFilterFormInitialValues,
  convertDatesForFilterPayload,
  convertToNormalTime,
  getStandardizedReportDates,
  reportTransformTimeForAPI
} from "app/data/helpers";
import {
  getResetFilterFormValues,
  buildReportFilterPayload
} from "app/data/helpers/ReportHelpers";
import { generateReportThunk } from "app/features/reports/reportsActions";
import {
  getAllKeysByAccountThunk,
  resetReportsFilterData,
  setReportsFilterData,
  setReportsDateValues
} from "app/features";
import { useTranslation } from "react-i18next";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import ReportEmptyState from "components/common/reports/ReportEmptyState";
import { selectAccountKeysState } from "app/data/selectors";
import { TOTAL_CO2_REPORT_TABLE_COLUMNS } from "app/data/constants/Reports/totalCo2ReportsConstants";
import ReportsCommonFilterForm from "components/forms/Reports/ReportsCommonFilterForm";
import TotalCO2ReportExpendableRowCard from "components/cards/ReportCards/TotalCO2ReportExpendableRowCard.tsx/TotalCO2ReportExpendableRowCard";

const TotalCO2Reports: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    handleOnChangeReportFilterForm,
    reportsFilterData,
    filterValues,
    setFilterValues,
    mergedObjectOptions,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates
  } = useReportCommonStates();

  const [openModal, setOpenModal] = useState(false);
  const { applyInactiveFilter, statusLookupsReady, showInactiveObjects } =
    useReportInactiveObjects();
  const tablePending = reportsLoading || !statusLookupsReady;

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const { formattedData, isShowKeyCol } = useMemo(() => {
    const formatted = commonReportsDataFormatter(
      localReport,
      "name",
      dateFormat
    );
    return {
      ...formatted,
      formattedData: applyInactiveFilter(formatted?.formattedData)
    };
  }, [localReport, dateFormat, applyInactiveFilter]);
  const tableColumns = useMemo(
    () => TOTAL_CO2_REPORT_TABLE_COLUMNS(t, isShowKeyCol),
    [t, isShowKeyCol]
  );

  const [apiPayload, setApiPayload] = useState(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? reportsFilterData
      : null
  );

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const co2ReportFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    setFilterValues(values);

    const startDate = new Date(dateValues.startDate);
    const endDate = new Date(dateValues.endDate);

    const { formattedStartDate, formattedEndDate } =
      convertDatesForFilterPayload(dateValues);

    const payload = {
      ...(apiPayload ?? {}),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...values,
      object: values.assignedToAllObjects ? [] : values.object,
      reportType: 7,
      daysOfWeek:
        values.daysOfWeek.length === 7 ? [] : values.daysOfWeek.map(Number),
      startTime: reportTransformTimeForAPI(
        values.startTime + ":00",
        startDate,
        false
      ),
      endTime: reportTransformTimeForAPI(
        values.endTime + ":00",
        endDate,
        true,
        startDate
      )
    };

    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const handleDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  };

  const handleDateRangeSubmit = () => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    const updatedFilterValues = { ...filterValues };

    const payload = {
      ...(apiPayload ?? {}),
      startDate,
      endDate,
      ...updatedFilterValues,
      object: filterValues.assignedToAllObjects
        ? []
        : (filterValues.object ?? []),
      reportType: 7
    };

    setApiPayload(payload);
  };

  const handleLoadReport = () => {
    const payload = buildReportFilterPayload({
      values: filterValues,
      dateValues,
      reportType: 7,
      normalizeDates: false,
      startDateOverride: convertToNormalTime(dateValues.startDate),
      endDateOverride: convertToNormalTime(dateValues.endDate, true)
    });

    setApiPayload(payload);
    dispatch(setReportsFilterData(payload));
    dispatch(generateReportThunk(payload));
  };

  useEffect(() => {
    // dispatch(resetReports());
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    setLocalReport(report);
  }, [report]);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.totalCo2Report")}
        disabled={reportsLoading}
      />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          disableExportButton={!formattedData?.length}
          reportType={7}
          reportExportTypeValue={2}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          hideExportButton={true}
          handleLoadReport={handleLoadReport}
          formattedData={formattedData}
        />
        <ReportsCommonFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={co2ReportFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={mergedObjectOptions}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          theme={theme}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          isCO2InnerForm
          formInitialValues={getHydratedFormInitialValues(
            co2ReportsFilterFormInitialValues
          )}
          formValidations={commonReportsFilterFormValidationSchema}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(co2ReportsFilterFormInitialValues)
            );
          }}
        />
        {/* <CO2ReportsFilterForm /> */}
        <Table
          columns={tableColumns}
          data={formattedData}
          progressPending={tablePending}
          noDataComponent={!tablePending ? <ReportEmptyState /> : null}
          expandableRows
          expandOnRowClicked
          expandableRowsComponent={TotalCO2ReportExpendableRowCard}
          expandableRowsComponentProps={{ isShowKeyCol: isShowKeyCol }}
          pagination={false}
          nestedRowPadding={"16px"}
          headerPadding={"50px"}
          translator={t}
        />
      </Box>
    </Box>
  );
};

export default TotalCO2Reports;
